// Rollup 100% client-side e PURO (sem imports de framework) a partir de duas listas de avaliações
// já filtradas no fetch: pendentes (consulta de relatório concluída, por enviar) e enviadas no
// período. Chave de rollup = directus_users.id do psicólogo da consulta (report_session.technician.user_id).
//
// Decisão load-bearing (validada): a "consulta de avaliação" é a sessão de relatório com o psicólogo;
// o relógio (dias à espera / tempo de envio) conta SEMPRE a partir do dia dessa consulta
// (report_session.start_date), em dias de calendário e em fuso de Lisboa. "Em atraso" = mais de
// OVERDUE_DAYS dias. Os pendentes nunca dependem do período (são stock atual).

// "Em atraso" = por enviar há mais de 10 dias (os casos a vermelho no semáforo).
export const OVERDUE_DAYS = 10;

export const UNKNOWN_TECHNICIAN_KEY = "__sem_tecnico__";
export const UNKNOWN_TECHNICIAN_NAME = "Sem técnico atribuído";

export type RawTechnicianRef = { user_id: string | null } | null;
export type RawReportSession = { start_date: string | null; service: number | null; technician: RawTechnicianRef } | null;

export type RawEvaluation = {
    id: number;
    evaluation_date_sent: string | null;
    report_session: RawReportSession;
    treatment: { id: number | null } | null;
};

export type PersonInfo = {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
};

export type PeopleById = Record<string, PersonInfo>;

export type PendingCaseRow = {
    evaluationId: number;
    treatmentId: number | null;
    technicianUserId: string | null;
    technicianKey: string;
    technicianName: string;
    reportServiceId: number | null;
    consultationDayKey: string;
    daysWaiting: number;
    isOverdue: boolean;
};

export type SentCaseRow = {
    evaluationId: number;
    technicianUserId: string | null;
    technicianKey: string;
    technicianName: string;
    reportServiceId: number | null;
    tempoEnvioDays: number | null;
};

export type TechnicianStatsRow = {
    technicianKey: string;
    displayName: string;
    emAtraso: number;
    pendentes: number;
    feitos: number;
    tempoMedioDays: number | null;
};

export type SummaryTotals = {
    emAtraso: number;
    pendentes: number;
    feitos: number;
    tempoMedioDays: number | null;
};

// Dia em fuso de Lisboa (yyyy-MM-dd; en-CA formata em ISO). Uma consulta perto da meia-noite cai no
// dia certo mesmo com mudança de hora (DST).
const lisbonDayFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Lisbon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

export const lisbonDayKey = (isoTimestamp: string | null | undefined) => {
    if (!isoTimestamp) return null;
    const dayKey = lisbonDayFormatter.format(new Date(isoTimestamp));
    return dayKey;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

// Dias de calendário entre dois dias (yyyy-MM-dd), ancorados à meia-noite UTC para evitar o salto
// de hora de verão (a diferença é sempre um nº inteiro de dias).
export const calendarDaysBetween = (fromDayKey: string, toDayKey: string) => {
    const fromMs = Date.parse(`${fromDayKey}T00:00:00Z`);
    const toMs = Date.parse(`${toDayKey}T00:00:00Z`);
    const days = Math.round((toMs - fromMs) / MS_PER_DAY);
    return days;
};

const displayNameOf = (person: PersonInfo | undefined) => {
    if (!person) return UNKNOWN_TECHNICIAN_NAME;
    const composedName = [person.first_name, person.last_name].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    const displayName = composedName || person.email || UNKNOWN_TECHNICIAN_NAME;
    return displayName;
};

const technicianKeyOf = (technicianUserId: string | null) => {
    const key = technicianUserId || UNKNOWN_TECHNICIAN_KEY;
    return key;
};

// Pendentes → uma linha por caso, da consulta mais antiga para a mais recente (mais dias à espera no topo).
export const derivePendingCases = (pendingEvaluations: Array<RawEvaluation>, peopleById: PeopleById, todayDayKey: string) => {
    const rows: Array<PendingCaseRow> = [];
    pendingEvaluations.forEach((evaluation) => {
        const consultationDayKey = lisbonDayKey(evaluation.report_session?.start_date);
        if (!consultationDayKey) return;
        const rawDays = calendarDaysBetween(consultationDayKey, todayDayKey);
        const daysWaiting = rawDays > 0 ? rawDays : 0;
        const technicianUserId = evaluation.report_session?.technician?.user_id || null;
        const row = {
            evaluationId: evaluation.id,
            treatmentId: evaluation.treatment?.id ?? null,
            technicianUserId,
            technicianKey: technicianKeyOf(technicianUserId),
            technicianName: displayNameOf(peopleById[technicianUserId || ""]),
            reportServiceId: evaluation.report_session?.service ?? null,
            consultationDayKey,
            daysWaiting,
            isOverdue: daysWaiting > OVERDUE_DAYS,
        };
        rows.push(row);
    });
    rows.sort((firstRow, secondRow) => {
        if (firstRow.consultationDayKey !== secondRow.consultationDayKey) {
            return firstRow.consultationDayKey < secondRow.consultationDayKey ? -1 : 1;
        }
        return firstRow.evaluationId - secondRow.evaluationId;
    });
    return rows;
};

// Enviadas no período → uma linha por caso. Conta sempre como "feito"; o tempo de envio fica null
// quando falta a data da consulta ou de envio (problema de dados, excluído só da média).
export const deriveSentCases = (sentEvaluations: Array<RawEvaluation>, peopleById: PeopleById) => {
    const rows: Array<SentCaseRow> = [];
    sentEvaluations.forEach((evaluation) => {
        const consultationDayKey = lisbonDayKey(evaluation.report_session?.start_date);
        const sentDayKey = lisbonDayKey(evaluation.evaluation_date_sent);
        const tempoEnvioDays =
            consultationDayKey && sentDayKey ? Math.max(0, calendarDaysBetween(consultationDayKey, sentDayKey)) : null;
        const technicianUserId = evaluation.report_session?.technician?.user_id || null;
        const row = {
            evaluationId: evaluation.id,
            technicianUserId,
            technicianKey: technicianKeyOf(technicianUserId),
            technicianName: displayNameOf(peopleById[technicianUserId || ""]),
            reportServiceId: evaluation.report_session?.service ?? null,
            tempoEnvioDays,
        };
        rows.push(row);
    });
    return rows;
};

type TechnicianAccumulator = {
    technicianKey: string;
    displayName: string;
    emAtraso: number;
    pendentes: number;
    feitos: number;
    tempoSum: number;
    tempoCount: number;
};

// Tabela por técnico: pendentes/em atraso são stock atual; feitos/tempo médio são do período.
export const technicianStatsRows = (pendingCases: Array<PendingCaseRow>, sentCases: Array<SentCaseRow>) => {
    const accumulatorByKey = new Map<string, TechnicianAccumulator>();
    const ensureAccumulator = (technicianKey: string, displayName: string) => {
        const existing = accumulatorByKey.get(technicianKey);
        if (existing) return existing;
        const created = { technicianKey, displayName, emAtraso: 0, pendentes: 0, feitos: 0, tempoSum: 0, tempoCount: 0 };
        accumulatorByKey.set(technicianKey, created);
        return created;
    };

    pendingCases.forEach((pendingCase) => {
        const accumulator = ensureAccumulator(pendingCase.technicianKey, pendingCase.technicianName);
        accumulator.pendentes += 1;
        if (pendingCase.isOverdue) accumulator.emAtraso += 1;
    });
    sentCases.forEach((sentCase) => {
        const accumulator = ensureAccumulator(sentCase.technicianKey, sentCase.technicianName);
        accumulator.feitos += 1;
        if (sentCase.tempoEnvioDays !== null) {
            accumulator.tempoSum += sentCase.tempoEnvioDays;
            accumulator.tempoCount += 1;
        }
    });

    const rows: Array<TechnicianStatsRow> = Array.from(accumulatorByKey.values()).map((accumulator) => {
        const tempoMedioDays = accumulator.tempoCount > 0 ? accumulator.tempoSum / accumulator.tempoCount : null;
        const row = {
            technicianKey: accumulator.technicianKey,
            displayName: accumulator.displayName,
            emAtraso: accumulator.emAtraso,
            pendentes: accumulator.pendentes,
            feitos: accumulator.feitos,
            tempoMedioDays,
        };
        return row;
    });
    rows.sort((firstRow, secondRow) => {
        if (secondRow.emAtraso !== firstRow.emAtraso) return secondRow.emAtraso - firstRow.emAtraso;
        if (secondRow.pendentes !== firstRow.pendentes) return secondRow.pendentes - firstRow.pendentes;
        if (secondRow.feitos !== firstRow.feitos) return secondRow.feitos - firstRow.feitos;
        return firstRow.displayName.localeCompare(secondRow.displayName, "pt");
    });
    return rows;
};

export const summaryTotals = (pendingCases: Array<PendingCaseRow>, sentCases: Array<SentCaseRow>) => {
    const pendentes = pendingCases.length;
    const emAtraso = pendingCases.filter((pendingCase) => pendingCase.isOverdue).length;
    const feitos = sentCases.length;
    const computableSentCases = sentCases.filter((sentCase) => sentCase.tempoEnvioDays !== null);
    const tempoSum = computableSentCases.reduce((sum, sentCase) => sum + (sentCase.tempoEnvioDays || 0), 0);
    const tempoMedioDays = computableSentCases.length > 0 ? tempoSum / computableSentCases.length : null;
    const totals = { emAtraso, pendentes, feitos, tempoMedioDays };
    return totals;
};
